import type {
  ApiError,
  ArchiveHour,
  CreateNvrReq,
  NvrInfo,
  PlaybackReq,
  PlaybackResp,
  PutNvrReq,
  StreamInfo,
  UserInfo,
} from './types';

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, init);
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const j = (await r.json()) as ApiError;
      if (j.error) msg = j.error;
    } catch (_) {
      /* ignore — body wasn't JSON */
    }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

export async function listStreams(): Promise<StreamInfo[]> {
  return jsonFetch<StreamInfo[]>('/api/streams');
}

export async function listNvrs(): Promise<NvrInfo[]> {
  return jsonFetch<NvrInfo[]>('/api/nvrs');
}

export async function createNvr(req: CreateNvrReq): Promise<void> {
  await jsonFetch('/api/nvrs', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
}

export async function putNvr(id: string, req: PutNvrReq): Promise<void> {
  await jsonFetch(`/api/nvrs/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
}

export async function patchNvr(
  id: string,
  patch: { name?: string; note?: string },
): Promise<void> {
  await jsonFetch(`/api/nvrs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function deleteNvr(id: string): Promise<void> {
  await jsonFetch(`/api/nvrs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function patchStreamLabel(name: string, label: string): Promise<void> {
  await jsonFetch(`/api/streams/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ label }),
  });
}

export async function startPlayback(req: PlaybackReq): Promise<PlaybackResp> {
  return jsonFetch<PlaybackResp>('/api/playback', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
}

export async function stopPlayback(playbackId: string): Promise<void> {
  await jsonFetch(`/api/playback/${encodeURIComponent(playbackId)}`, { method: 'DELETE' });
}

/** Browser local datetime-local string → Unix epoch seconds (string), per UNV spec. */
export function dtToUnix(localStr: string): string {
  return Math.floor(new Date(localStr).getTime() / 1000).toString();
}

/** Unix milliseconds → datetime-local input value. */
export function msToLocal(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Auth ────────────────────────────────────────────────────────

export type WhoAmI = { username: string; role: string; must_change: boolean };

export async function whoami(): Promise<WhoAmI | null> {
  const r = await fetch('/api/auth/whoami');
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return (await r.json()) as WhoAmI;
}

export async function login(username: string, password: string): Promise<WhoAmI> {
  return jsonFetch<WhoAmI>('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
}

export async function logout(): Promise<void> {
  await jsonFetch('/api/auth/logout', { method: 'POST' });
}

export async function changePassword(oldPw: string, newPw: string): Promise<void> {
  await jsonFetch('/api/auth/password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
  });
}

// ── Local archive (continuous recording) ────────────────────────

export async function listArchive(stream: string): Promise<ArchiveHour[]> {
  return jsonFetch<ArchiveHour[]>(`/api/archive/${encodeURIComponent(stream)}`);
}

/** URL that downloads the cut fragment as an MP4 attachment. */
/** Спрашивает состояние выгрузки; при отсутствии — запускает её в фоне. */
export async function clipState(
  stream: string,
  fromMs: number,
  toMs: number,
  h264: boolean,
): Promise<'ready' | 'building'> {
  // Именно 'true': разбор логического поля на сервере не принимает '1'.
  const r = await fetch(clipUrl(stream, fromMs, toMs, h264) + '&probe=true');
  if (!r.ok && r.status !== 202) {
    let msg = `HTTP ${r.status}`;
    try {
      const j = (await r.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch (_) {
      /* тело не JSON */
    }
    throw new Error(msg);
  }
  const j = (await r.json()) as { state?: string };
  return j.state === 'ready' ? 'ready' : 'building';
}

export function clipUrl(stream: string, fromMs: number, toMs: number, h264: boolean): string {
  const q = new URLSearchParams({ from: String(fromMs), to: String(toMs) });
  if (h264) q.set('h264', 'true');
  return `/api/clip/${encodeURIComponent(stream)}?${q.toString()}`;
}

// ── Управление пользователями (только для админа) ───────────────

export async function listUsers(): Promise<UserInfo[]> {
  return jsonFetch<UserInfo[]>('/api/users');
}

/** Без пароля сервер сгенерирует его сам и вернёт ОДИН раз. */
export async function createUser(
  name: string,
  role: string,
  password?: string,
): Promise<{ name: string; role: string; password: string | null }> {
  return jsonFetch('/api/users', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name, role, password: password || undefined }),
  });
}

export async function deleteUser(name: string): Promise<void> {
  await jsonFetch(`/api/users/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/** Сбрасывает пароль на новый случайный и возвращает его ОДИН раз. */
export async function resetUserPassword(name: string): Promise<{ password: string }> {
  return jsonFetch(`/api/users/${encodeURIComponent(name)}/password`, { method: 'POST' });
}

/** Меняет ключ подписи — разлогинивает вообще всех, включая себя. */
export async function rotateSecret(): Promise<void> {
  await jsonFetch('/api/auth/rotate-secret', { method: 'POST' });
}

/** Ограничить доступ пользователя. Оба списка пустые = доступ ко всему. */
export async function setUserAccess(
  name: string,
  nvrs: string[],
  streams: string[],
): Promise<void> {
  await jsonFetch(`/api/users/${encodeURIComponent(name)}/access`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ nvrs, streams }),
  });
}
